/**
 * THE CALLER'S AUDIO: where the words a call-eval run says come from (plan §12.3).
 *
 * DECIDED: the audio is made ON THE OPERATOR'S MACHINE, never by WayAI's backend, and it reaches
 * the call only as the runner's own WebRTC track to the provider. The backend is never on the
 * call's audio path (settled decision 4, as amended by decision 22): it creates the call and reads
 * its record, and no audio crosses it. Two sources, one per run:
 *
 * - PRE-RECORDED CLIPS (`--clips <dir>`, or a line's `clip` in the plan): 16-bit PCM WAV files,
 *   any sample rate, mono or stereo — line N is `<dir>/<N>.wav`. Record them, or make them with
 *   any TTS the operator likes (macOS: `say -v Luciana -o 1.wav --data-format=LEI16@24000 "…"`).
 *   The same file every run, so a suite's pass rates measure the call, not the synthesis.
 * - THE OPERATOR'S OWN TTS KEY (`OPENAI_API_KEY` in the environment): each line is synthesized
 *   once with OpenAI's speech endpoint and CACHED on disk by model, voice and text, so later runs
 *   replay the same audio and spend nothing. The key is the operator's, read from their shell;
 *   the hub's own TTS connections stay on the backend, which never hands a credential out.
 *   The cache lives with the CLI's other local state, under `~/.wayai/call-clips`.
 *
 * Either way a clip is 16-bit mono PCM at its own rate; the media pump resamples it to its own.
 */

import { createHash } from 'node:crypto';
import { mkdirSync, readFileSync, renameSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, isAbsolute, join, resolve } from 'node:path';
import type { CallLine } from './script.js';

export interface CallerClip {
  samples: Int16Array;
  sampleRate: number;
}

export interface CallerAudioSource {
  /** Where the audio comes from, for the report. */
  readonly describe: string;
  clipFor(line: CallLine): Promise<CallerClip>;
}

/**
 * A 16-bit PCM WAV file as mono samples. Throws on anything else (compressed or 8/24/32-bit
 * audio, a truncated file): a clip the runner cannot play exactly is not played at all.
 */
export function parseWav(buf: Buffer, name = 'clip'): CallerClip {
  if (buf.length < 12 || buf.toString('ascii', 0, 4) !== 'RIFF' || buf.toString('ascii', 8, 12) !== 'WAVE') {
    throw new Error(`${name} is not a WAV file.`);
  }
  let offset = 12;
  let format: { channels: number; sampleRate: number; bits: number; code: number } | null = null;
  while (offset + 8 <= buf.length) {
    const id = buf.toString('ascii', offset, offset + 4);
    const size = buf.readUInt32LE(offset + 4);
    const body = offset + 8;
    if (id === 'fmt ') {
      format = {
        code: buf.readUInt16LE(body),
        channels: buf.readUInt16LE(body + 2),
        sampleRate: buf.readUInt32LE(body + 4),
        bits: buf.readUInt16LE(body + 14),
      };
    } else if (id === 'data') {
      if (!format) throw new Error(`${name}: the WAV data comes before its format.`);
      // 1 = PCM; 0xFFFE = WAVE_FORMAT_EXTENSIBLE, which `say` and others write for plain PCM.
      if ((format.code !== 1 && format.code !== 0xfffe) || format.bits !== 16) {
        throw new Error(`${name} must be 16-bit PCM (it is format ${format.code}, ${format.bits}-bit).`);
      }
      if (format.channels < 1 || format.sampleRate < 8_000) throw new Error(`${name} has an unusable format.`);
      const end = Math.min(buf.length, body + size);
      const frames = Math.floor((end - body) / (2 * format.channels));
      const samples = new Int16Array(frames);
      for (let i = 0; i < frames; i++) {
        let sum = 0;
        for (let ch = 0; ch < format.channels; ch++) sum += buf.readInt16LE(body + 2 * (i * format.channels + ch));
        samples[i] = Math.round(sum / format.channels);
      }
      return { samples, sampleRate: format.sampleRate };
    }
    offset = body + size + (size % 2);
  }
  throw new Error(`${name} holds no audio data.`);
}

/** Clips from a directory: a line's plan `clip` (relative to the plan file), else `<dir>/<n>.wav`. */
export class ClipDirectorySource implements CallerAudioSource {
  readonly describe: string;

  constructor(
    private readonly dir: string | null,
    /** Where the plan file sits: a relative `clip` path is read from there. */
    private readonly planDir: string,
  ) {
    this.describe = dir ? `pre-recorded clips in ${dir}` : 'pre-recorded clips named by the plan';
  }

  async clipFor(line: CallLine): Promise<CallerClip> {
    const path = line.clip
      ? (isAbsolute(line.clip) ? line.clip : resolve(this.planDir, line.clip))
      : this.dir ? join(this.dir, `${line.n}.wav`) : null;
    if (!path) throw new Error(`Line ${line.n} has no clip: give --clips <dir>, or a "clip" for it in the plan.`);
    let buf: Buffer;
    try {
      buf = readFileSync(path);
    } catch {
      throw new Error(`Line ${line.n}'s clip ${path} cannot be read.`);
    }
    return parseWav(buf, path);
  }
}

/** OpenAI's speech endpoint returns raw PCM at this rate, 16-bit mono. */
export const OPENAI_PCM_SAMPLE_RATE = 24_000;

export const DEFAULT_TTS_MODEL = 'gpt-4o-mini-tts';
export const DEFAULT_TTS_VOICE = 'coral';

/** The default cache: survives runs, so a suite synthesizes each line once. */
export function defaultClipCacheDir(): string {
  return join(homedir(), '.wayai', 'call-clips');
}

export interface OpenAiTtsOptions {
  apiKey: string;
  model?: string;
  voice?: string;
  cacheDir?: string;
  fetch?: typeof fetch;
}

/** Each line synthesized once with the operator's own OpenAI key, then replayed from the cache. */
export class OpenAiTtsSource implements CallerAudioSource {
  readonly describe: string;
  private readonly model: string;
  private readonly voice: string;
  private readonly cacheDir: string;
  private readonly doFetch: typeof fetch;
  /** Syntheses in flight, by cache file: two lines with the same words pay for one. */
  private readonly inFlight = new Map<string, Promise<CallerClip>>();

  constructor(private readonly options: OpenAiTtsOptions) {
    this.model = options.model ?? DEFAULT_TTS_MODEL;
    this.voice = options.voice ?? DEFAULT_TTS_VOICE;
    this.cacheDir = options.cacheDir ?? defaultClipCacheDir();
    this.doFetch = options.fetch ?? fetch;
    this.describe = `OpenAI ${this.model} (${this.voice}), cached in ${this.cacheDir}`;
  }

  /** The cache file for a text: one per model, voice and exact text. */
  cachePath(text: string): string {
    const key = createHash('sha256').update(`${this.model}\n${this.voice}\n${text}`).digest('hex');
    return join(this.cacheDir, `${key}.pcm`);
  }

  clipFor(line: CallLine): Promise<CallerClip> {
    const path = this.cachePath(line.say);
    let pending = this.inFlight.get(path);
    if (!pending) {
      pending = this.readOrSynthesize(line, path);
      this.inFlight.set(path, pending);
    }
    return pending;
  }

  private async readOrSynthesize(line: CallLine, path: string): Promise<CallerClip> {
    try {
      return pcmClip(readFileSync(path));
    } catch {
      // Not cached yet.
    }
    const res = await this.doFetch('https://api.openai.com/v1/audio/speech', {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.options.apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: this.model, voice: this.voice, input: line.say, response_format: 'pcm' }),
    });
    if (!res.ok) {
      // The body is OpenAI's error, never echoed: it can quote the request.
      await res.body?.cancel();
      throw new Error(`OpenAI speech synthesis failed for line ${line.n} (HTTP ${res.status}).`);
    }
    const buf = Buffer.from(await res.arrayBuffer());
    if (buf.length < 2) throw new Error(`OpenAI speech synthesis returned no audio for line ${line.n}.`);
    mkdirSync(dirname(path), { recursive: true });
    const tmp = `${path}.${process.pid}.tmp`;
    writeFileSync(tmp, buf);
    renameSync(tmp, path);
    return pcmClip(buf);
  }
}

function pcmClip(buf: Buffer): CallerClip {
  const samples = new Int16Array(Math.floor(buf.length / 2));
  for (let i = 0; i < samples.length; i++) samples[i] = buf.readInt16LE(2 * i);
  return { samples, sampleRate: OPENAI_PCM_SAMPLE_RATE };
}

/**
 * How long a clip runs on after its last word: the silence at its end, in 10 ms frames quieter
 * than a tenth of its loudest (-20 dB). The voice hears the caller stop where the words stop, so
 * this is how much earlier than the clip's end the caller's end of speech is. A silent clip has none.
 */
export function trailingSilenceMs(clip: CallerClip): number {
  const frame = Math.max(1, Math.round(clip.sampleRate / 100));
  const frames = Math.ceil(clip.samples.length / frame);
  const rms = (f: number): number => {
    let sum = 0;
    const end = Math.min(clip.samples.length, (f + 1) * frame);
    for (let i = f * frame; i < end; i++) sum += clip.samples[i] * clip.samples[i];
    return Math.sqrt(sum / Math.max(1, end - f * frame));
  };
  let loudest = 0;
  for (let f = 0; f < frames; f++) loudest = Math.max(loudest, rms(f));
  if (loudest === 0) return 0;
  let last = frames - 1;
  while (last > 0 && rms(last) < loudest / 10) last--;
  const spokenSamples = Math.min(clip.samples.length, (last + 1) * frame);
  return ((clip.samples.length - spokenSamples) * 1000) / clip.sampleRate;
}

/** Linear resampling to `rate`: enough for speech into an Opus encoder. */
export function resample(clip: CallerClip, rate: number): Int16Array {
  if (clip.sampleRate === rate) return clip.samples;
  const out = new Int16Array(Math.max(1, Math.round(clip.samples.length * rate / clip.sampleRate)));
  const step = clip.sampleRate / rate;
  for (let i = 0; i < out.length; i++) {
    const pos = i * step;
    const i0 = Math.min(clip.samples.length - 1, Math.floor(pos));
    const i1 = Math.min(clip.samples.length - 1, i0 + 1);
    const frac = pos - i0;
    out[i] = Math.round(clip.samples[i0] * (1 - frac) + clip.samples[i1] * frac);
  }
  return out;
}
